import { useEffect, useRef, useState } from 'react';
import { haptic } from '../lib/telegram';

const HOLD_MS = 900;

/**
 * Irreversible actions confirm by holding: the slab fills like a vein
 * running through stone. Keyboard users confirm with Enter/Space held or
 * a double press, so the gesture never blocks access.
 */
export function HoldButton({ label, busyLabel, busy, onConfirm, disabled }: { label: string; busyLabel: string; busy?: boolean; onConfirm: () => void; disabled?: boolean }) {
  const [progress, setProgress] = useState(0);
  const raf = useRef(0);
  const start = useRef(0);
  const fired = useRef(false);

  const stop = () => {
    cancelAnimationFrame(raf.current);
    if (!fired.current) setProgress(0);
  };

  const begin = () => {
    if (disabled || busy) return;
    fired.current = false;
    start.current = performance.now();
    haptic.tap();
    const tick = (now: number) => {
      const p = Math.min(1, (now - start.current) / HOLD_MS);
      setProgress(p);
      if (p >= 1) {
        fired.current = true;
        haptic.press();
        onConfirm();
        return;
      }
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
  };

  useEffect(() => () => cancelAnimationFrame(raf.current), []);
  useEffect(() => {
    if (!busy) {
      fired.current = false;
      setProgress(0);
    }
  }, [busy]);

  return (
    <button
      type="button"
      className={`hold${busy ? ' is-busy' : ''}`}
      disabled={disabled}
      aria-busy={busy || undefined}
      onPointerDown={(e) => {
        (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
        begin();
      }}
      onPointerUp={stop}
      onPointerCancel={stop}
      onPointerLeave={stop}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !e.repeat) {
          e.preventDefault();
          begin();
        }
      }}
      onKeyUp={stop}
      onContextMenu={(e) => e.preventDefault()}
    >
      <span className="hold__label">{busy ? busyLabel : label}</span>
      {/* The fill sweeps a deeper coral under the label while the hold runs. */}
      <span className="hold__fill" style={{ clipPath: `inset(0 ${(1 - (busy ? 1 : progress)) * 100}% 0 0)` }} aria-hidden="true">
        <span className="hold__label">{busy ? busyLabel : label}</span>
      </span>
    </button>
  );
}
